import React, { useContext, useEffect, useState, useCallback } from "react";
import Select from "react-select";
import { Card, CardBody, Button, FormGroup, Label, Input, Spinner } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { PostData } from "../../../services/FetchData";
import { ProductContext } from "../../../store";
import { toast } from "react-toastify";

const DRUPAL_BASE = "ms3";

function searchableFieldOptions(schemaFields) {
	if (!Array.isArray(schemaFields)) return [];
	return schemaFields.filter((f) => {
		const t = String(f.type || "").toLowerCase();
		const name = f.name != null ? String(f.name).trim() : "";
		if (!name || name.toLowerCase() === "id") return false;
		return t === "string" || t === "string[]";
	});
}

export default function SearchQueryFieldsSettings() {
	const { currentWebsiteId } = useContext(ProductContext);
	const websiteId = currentWebsiteId;
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [indexIds, setIndexIds] = useState([]);
	const [queryMap, setQueryMap] = useState({});
	const [useCustom, setUseCustom] = useState({});
	const [schemaByIndex, setSchemaByIndex] = useState({});

	const loadConfig = useCallback(() => {
		if (!websiteId) return;
		setLoading(true);
		PostData(DRUPAL_BASE, "api/v1/drupal/getQueryByConfig", { websiteId })
			.then((res) => {
				if (res?.status !== "success" || !res.data) {
					toast.error(res?.message || "Failed to load");
					return;
				}
				const { queryByConfigByIndexId, indexIds: ids } = res.data;
				const raw =
					queryByConfigByIndexId && typeof queryByConfigByIndexId === "object"
						? queryByConfigByIndexId
						: {};
				setIndexIds(Array.isArray(ids) ? ids : []);
				setQueryMap(raw);
				const uc = {};
				for (const id of ids || []) {
					uc[id] = Array.isArray(raw[id]) && raw[id].length > 0;
				}
				setUseCustom(uc);
			})
			.catch(() => toast.error("Failed to load"))
			.finally(() => setLoading(false));
	}, [websiteId]);

	useEffect(() => {
		loadConfig();
	}, [loadConfig]);

	useEffect(() => {
		if (!websiteId || !indexIds.length) return;
		Promise.all(
			indexIds.map((index_id) =>
				PostData(DRUPAL_BASE, "api/v1/drupal/collection-schema", {
					websiteId,
					index_id,
					index_label: index_id,
				})
					.then((r) => {
						const raw = r.fields || r.schema?.fields || [];
						return [index_id, Array.isArray(raw) ? raw : []];
					})
					.catch(() => [index_id, []])
			)
		).then((pairs) => {
			const next = {};
			pairs.forEach(([id, fields]) => {
				next[id] = fields;
			});
			setSchemaByIndex(next);
		});
	}, [websiteId, indexIds]);

	const toggleCustom = (id, custom) => {
		setUseCustom((prev) => ({ ...prev, [id]: custom }));
		if (!custom) {
			setQueryMap((prev) => {
				const next = { ...prev };
				delete next[id];
				return next;
			});
		} else {
			setQueryMap((prev) => {
				if (prev[id]?.length) return prev;
				const opts = searchableFieldOptions(schemaByIndex[id]);
				const first = opts[0]?.name || "";
				return { ...prev, [id]: first ? [{ field: first, weight: 1 }] : [] };
			});
		}
	};

	const addRow = (id) => {
		const opts = searchableFieldOptions(schemaByIndex[id]);
		const first = opts[0]?.name || "";
		setQueryMap((prev) => ({
			...prev,
			[id]: [...(prev[id] || []), { field: first, weight: 1 }],
		}));
	};

	const removeRow = (id, idx) => {
		setQueryMap((prev) => {
			const rows = [...(prev[id] || [])];
			rows.splice(idx, 1);
			const next = { ...prev };
			if (rows.length) next[id] = rows;
			else delete next[id];
			return next;
		});
	};

	const updateRow = (id, idx, key, val) => {
		setQueryMap((prev) => {
			const rows = [...(prev[id] || [])];
			if (!rows[idx]) return prev;
			if (key === "weight") {
				let w = parseInt(val, 10);
				if (Number.isNaN(w)) w = 1;
				w = Math.max(0, Math.min(127, w));
				rows[idx] = { ...rows[idx], weight: w };
			} else {
				rows[idx] = { ...rows[idx], field: val };
			}
			return { ...prev, [id]: rows };
		});
	};

	async function handleSave(e) {
		e.preventDefault();
		if (!websiteId) return;
		setSaving(true);
		try {
			const payload = {};
			for (const id of indexIds) {
				if (useCustom[id] && Array.isArray(queryMap[id]) && queryMap[id].length) {
					const cleaned = queryMap[id]
						.map((r) => ({
							field: String(r.field || "").trim(),
							weight: Math.max(0, Math.min(127, parseInt(r.weight, 10) || 1)),
						}))
						.filter((r) => r.field);
					if (cleaned.length) payload[id] = cleaned;
				}
			}
			const res = await PostData(DRUPAL_BASE, "api/v1/drupal/saveQueryByConfig", {
				websiteId,
				queryByConfigByIndexId: Object.keys(payload).length ? payload : null,
			});
			if (res?.status === "success") toast.success(res.message || "Saved");
			else toast.error(res?.message || "Failed to save");
		} catch (err) {
			toast.error(err?.message || "Failed to save");
		} finally {
			setSaving(false);
		}
	}

	if (loading) return <Spinner />;
	if (!websiteId) return <p>Select a website.</p>;

	return (
		<Card>
			<CardBody>
				<form onSubmit={handleSave}>
					<h2 className="h5 mb-2">Search fields &amp; weights</h2>
					<p className="text-muted small mb-4">
						Choose which fields are searched and how strongly each field is weighted, per index. This applies to{" "}
						<strong>autocomplete</strong> and <strong>instant search</strong>. Choose &quot;Default&quot; to use all
						searchable text fields from the index in the default order.
					</p>

					{indexIds.length === 0 && (
						<p className="text-muted">No indexes found. Configure autocomplete sections or instant search product/content indices first.</p>
					)}

					{indexIds.map((idx) => {
						const custom = !!useCustom[idx];
						const rows = queryMap[idx] || [];
						const opts = searchableFieldOptions(schemaByIndex[idx] || []);
						const fieldSelectOptions = opts.map((f) => ({ value: f.name, label: f.name }));

						return (
							<div key={idx} className="border rounded p-3 mb-4">
								<h3 className="h6 mb-3">
									Index: <code>{idx}</code>
								</h3>
								<FormGroup>
									<Label className="d-block">Query fields</Label>
									<Input
										type="select"
										value={custom ? "custom" : "default"}
										onChange={(e) => toggleCustom(idx, e.target.value === "custom")}
									>
										<option value="default">Default — all searchable string fields (from schema)</option>
										<option value="custom">Custom — choose fields and weights (0–127)</option>
									</Input>
								</FormGroup>

								{custom && (
									<>
										<p className="text-muted small">Earlier fields in the list still affect ranking when weights are equal.</p>
										{rows.length === 0 && (
											<p className="text-muted small mb-2">No rows yet. Load schema above or add a field row.</p>
										)}
										{rows.map((row, i) => (
											<div key={`${idx}-${i}`} className="d-flex flex-wrap align-items-end gap-2 mb-2">
												<FormGroup className="mb-0 flex-grow-1" style={{ minWidth: "240px" }}>
													<Label className="small mb-0" htmlFor={`query-field-${idx}-${i}`}>Field</Label>
													<div>
														<Select
															inputId={`query-field-${idx}-${i}`}
															className="react-select-container"
															classNamePrefix="react-select"
															options={fieldSelectOptions}
															value={(() => {
																const f = row.field ? String(row.field).trim() : "";
																if (!f) return null;
																const match = fieldSelectOptions.find((o) => o.value === f);
																return match || { value: f, label: `${f} (saved)` };
															})()}
															onChange={(opt) => updateRow(idx, i, "field", opt ? opt.value : "")}
															placeholder={fieldSelectOptions.length ? "Search or select field..." : "No string fields in schema"}
															isDisabled={fieldSelectOptions.length === 0 && !row.field}
															isClearable
															isSearchable
															menuPortalTarget={typeof document !== "undefined" ? document.body : null}
															menuPosition="fixed"
															styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
															noOptionsMessage={() => "No matching fields"}
														/>
													</div>
												</FormGroup>
												<FormGroup className="mb-0" style={{ width: "120px" }}>
													<Label className="small mb-0">Weight</Label>
													<Input
														type="number"
														min={0}
														max={127}
														value={row.weight ?? 1}
														onChange={(e) => updateRow(idx, i, "weight", e.target.value)}
													/>
												</FormGroup>
												<Button type="button" color="link" className="text-danger p-1 mb-1" onClick={() => removeRow(idx, i)}>
													<FontAwesomeIcon icon={faTrash} />
												</Button>
											</div>
										))}
										<Button type="button" outline color="secondary" size="sm" onClick={() => addRow(idx)}>
											<FontAwesomeIcon icon={faPlus} className="me-1" /> Add field
										</Button>
									</>
								)}
							</div>
						);
					})}

					<Button type="submit" color="primary" className="mt-2" disabled={saving}>
						{saving ? "Saving..." : "Save"}
					</Button>
				</form>
			</CardBody>
		</Card>
	);
}
